/* eslint-disable complexity */
import React, { ChangeEvent } from 'react';
import { FaChargingStation, FaGasPump, FaOilCan, FaPlug } from 'react-icons/fa';
import { ReactComponent as BikeIcon } from '../../assets/newsvg/bike.svg';
import { ReactComponent as CargoBikeIcon } from '../../assets/newsvg/bike-cargo.svg';
import Modal from '../../components/modals/Modal';
import { ReqEnum } from '../../const/requestStatus';
import { fetchCouriers } from '../../fetch/fetchCouriers';
import { fetchAccounts } from '../../fetch/fetchUsers';
import { settings } from '../../settings';
import { CarType } from '../../types/bundleTypes';
import { regionNames, RegionsList } from '../../types/orderTypes';
import analyticsWrapper from '../../utils/analyticsWrapper';
import { toggleElement } from '../../utils/array';
import {
  isOrganisationNumberValid,
  supportedCountries,
} from '../../utils/country';
import {
  fetchCompanyName,
  getOrgNumberStringAsString,
} from '../../utils/enhetsregisteretUtil';
import Fetch from '../../utils/fetch';
import { capitalize } from '../../utils/strings';
import {
  Account,
  CompensationModel,
  CourierTravelMode,
  User,
} from '../../types/userTypes';
import { EditCourierArea } from '../courierArea/EditCourierArea';
import {
  AccountsChooseOrCreateRadioButtons,
  IsCompanyInAccountsList,
  IsCompanyInTestAccountsList,
  ShowTestAccountIfItExists,
} from '../create-user/CreateUser';
import { AccountRow } from './AccountRow';
import {
  EditUser,
  EmailRow,
  EmailVerified,
  ErrorMsg,
  NotesRow,
  RoleRow,
  ShowCourierIdRow,
} from './EditAdminUserModal';
import { SwedenInfo, UpdateFeedback } from './EditSenderUserModal';
import './EditUserModal.scss';
import { LockUser } from './LockUser';
import { postUpdateUser } from './postUpdateUser';
import { IsThereAnyErrors, validateCourierFields } from './validateFields';
import { sendPasswordResetLink } from './sendPasswordResetLink';
import { EditFormGroupModal } from './EditFormGroupModal';

const EDIT_USER_MODIFIED_CLASS = 'edit-user-modal__modified';

enum KindOfAccountSelection {
  CREATE,
  CHOOSE,
}

interface State {
  user: EditUser;
  courierId: string;
  currentPlateNumbers: string[];
  courierEtaDiff?: number;
  userUpdated: boolean;
  updatingUser: boolean;
  currentNotes: string;
  fieldErrors: {
    [key: string]: ErrorMsg;
  };
  errors: ErrorMsg[];
  fetchingAccounts: boolean;
  accounts: Account[];
  accountIdSelected?: number;
  companyNameFromFetch: string;

  reqCompanyNameFromFetch: ReqEnum;
  isCompanyInTestAccountsList: boolean;
  isCompanyInAccountsList: boolean;
  kindOfAccountSelection: KindOfAccountSelection;
  resettingPassword: boolean;
}

interface Props {
  onCloseModal: (updated: boolean) => void;
  user: User;
}

function formatEtaOffset(seconds: number): string {
  const sign = Math.sign(seconds);
  seconds = Math.abs(seconds);
  const minutes: string | number = Math.floor(seconds / 60);
  const secs: string | number = seconds - minutes * 60;
  const minusPlus = sign < 0 ? 'less' : 'more';

  if (seconds === 0) {
    return `The courier takes the standard time per stop.`;
  } else if (minutes === 0) {
    return `The courier takes ${secs} seconds ${minusPlus} per stop.`;
  } else if (secs === 0) {
    return `The courier takes ${minutes} minutes ${minusPlus} per stop.`;
  } else {
    return `The courier takes ${minutes} minutes and ${secs} seconds ${minusPlus} per stop.`;
  }
}

function onFocusEvent(keyName: string, label: string): void {
  analyticsWrapper.event({
    category: 'edit-courier-user',
    action: `change-${keyName}`,
    label,
  });
}

function EditInput({
  value,
  propsValue,
  errorMsg,
  keyName,
  updateStringMethod,
  title,
  type,
  placeholder,
}: {
  value: string;
  propsValue: string;
  errorMsg: { message: string };
  keyName: string;
  updateStringMethod: (keyName: string, name: string) => void;
  title: string;
  type: 'tel' | 'text' | 'email';
  placeholder: string;
}): JSX.Element {
  return (
    <EditFormGroupModal hasError={!!errorMsg}>
      <label>
        <span className="edit-user-modal__form-label">{`${title}`}</span>
        <input
          type={type}
          data-lpignore="true"
          placeholder={placeholder}
          name={keyName}
          value={value}
          onChange={event => updateStringMethod(keyName, event.target.value)}
          onFocus={() => onFocusEvent(keyName, 'input-box')}
          className={
            value && value !== propsValue ? EDIT_USER_MODIFIED_CLASS : undefined
          }
          autoComplete="none"
        />
      </label>
      {errorMsg && <em>{errorMsg.message}</em>}
    </EditFormGroupModal>
  );
}

function ShowCourierArea({
  preferredGeoZonesFieldErrors,
  updateCourierAreaMethod,
  courierAreaList,
}: {
  preferredGeoZonesFieldErrors: ErrorMsg;
  updateCourierAreaMethod: (newList: string[]) => void;
  courierAreaList: string[];
}): JSX.Element {
  return (
    <EditFormGroupModal hasError={!!preferredGeoZonesFieldErrors}>
      <label>
        <span className="edit-user-modal__form-label">Courier Area</span>
        <div className="edit-user-modal__form-dropdown">
          <EditCourierArea
            areaList={courierAreaList}
            addAreaListMethod={updateCourierAreaMethod} // updateCourierAreaMethod
          />
        </div>
      </label>
      {preferredGeoZonesFieldErrors && (
        <em>{preferredGeoZonesFieldErrors.message}</em>
      )}
    </EditFormGroupModal>
  );
}

function ShowVehicle({
  currentPlateNumbers,
  plateNumbers,
  carType,
  updateUser,
}: {
  currentPlateNumbers: string[];
  plateNumbers?: string[];
  carType: CarType;
  updateUser: (updatedUserFields: Partial<EditUser>) => void;
}): JSX.Element {
  return (
    <div className="edit-user-modal__form-group">
      <label>
        <span className="edit-user-modal__form-label">Plate Numbers</span>
        <input
          type="text"
          data-lpignore="true"
          placeholder="e.g. DR 7123, BT 1234"
          name="plateNumbers"
          value={currentPlateNumbers && plateNumbers ? plateNumbers.join() : ''}
          onChange={event => {
            const value = event.target.value;
            const numbers = value.split(',');
            updateUser({ plateNumbers: numbers });
          }}
          className={
            currentPlateNumbers &&
            plateNumbers &&
            plateNumbers !== currentPlateNumbers
              ? EDIT_USER_MODIFIED_CLASS
              : undefined
          }
          readOnly={!currentPlateNumbers}
          autoComplete="none"
        />
      </label>
      <label style={{ margin: '12px auto' }}>
        <span className="edit-user-modal__form-label">Vehicle</span>
        <div
          className="edit-user-modal__form-radio"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ marginBottom: '16px' }}>
            <label>
              <input
                type="radio"
                placeholder=""
                name="carTypeCargoBike"
                checked={carType === CarType.CARGO_BIKE}
                onChange={() =>
                  updateUser({
                    travelMode: CourierTravelMode.Bike,
                    carType: CarType.CARGO_BIKE,
                  })
                }
              />
              <span>
                <em style={{ whiteSpace: 'nowrap' }}>Cargo Bike</em>{' '}
                <span className="edit-user__form-icon">
                  <CargoBikeIcon />
                </span>
              </span>
            </label>
            <label>
              <input
                type="radio"
                placeholder=""
                name="carTypeNormalBike"
                checked={carType === CarType.NORMAL_BIKE}
                onChange={() =>
                  updateUser({
                    travelMode: CourierTravelMode.Bike,
                    carType: CarType.NORMAL_BIKE,
                  })
                }
              />
              <span>
                <em style={{ whiteSpace: 'nowrap' }}>Normal Bike</em>{' '}
                <span className="edit-user__form-icon">
                  <BikeIcon />
                </span>
              </span>
            </label>
          </div>
          <div>
            <label>
              <input
                type="radio"
                placeholder=""
                name="carTypeElectric"
                checked={carType === CarType.ELECTRIC}
                onChange={() =>
                  updateUser({
                    travelMode: CourierTravelMode.Car,
                    carType: CarType.ELECTRIC,
                  })
                }
              />
              <span>
                Electric{' '}
                <span className="edit-user__form-icon">
                  <FaPlug />
                </span>
              </span>
            </label>
            <label>
              {' '}
              <input
                type="radio"
                placeholder=""
                name="carTypeHybrid"
                checked={carType === CarType.HYBRID}
                onChange={() =>
                  updateUser({
                    travelMode: CourierTravelMode.Car,
                    carType: CarType.HYBRID,
                  })
                }
              />
              <span>
                Hybrid{' '}
                <span className="edit-user__form-icon">
                  <FaChargingStation />
                </span>
              </span>
            </label>
            <label>
              {' '}
              <input
                type="radio"
                placeholder=""
                name="carTypePetrol"
                checked={carType === CarType.PETROL}
                onChange={() =>
                  updateUser({
                    travelMode: CourierTravelMode.Car,
                    carType: CarType.PETROL,
                  })
                }
              />
              <span>
                Petrol{' '}
                <span className="edit-user__form-icon">
                  <FaGasPump />
                </span>
              </span>
            </label>
            <label>
              {' '}
              <input
                type="radio"
                placeholder=""
                name="carTypeDiesel"
                checked={carType === CarType.DIESEL}
                onChange={event =>
                  updateUser({
                    travelMode: CourierTravelMode.Car,
                    carType: CarType.DIESEL,
                  })
                }
              />
              <span>
                Diesel{' '}
                <span className="create-user__form-icon">
                  <FaOilCan />
                </span>
              </span>
            </label>
          </div>
        </div>
      </label>
    </div>
  );
}

export class EditCourierUserModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      userUpdated: false,
      updatingUser: false,
      courierId: '…',
      // getting filled when fetch courier is done!
      currentPlateNumbers: null,
      currentNotes: '',
      user: {
        ...props.user,
        newEmail: '',
        companyName: props.user.companyName || '',
        companyNumber: props.user.companyNumber || '',
        paymentByInvoiceAllowed: props.user.paymentByInvoiceAllowed,
        travelMode: props.user.travelMode || CourierTravelMode.None,
        plateNumbers: null,
        etaOffsetSeconds: props.user.etaOffsetSeconds || 0,
        etaOffsetSecondsInput: (props.user.etaOffsetSeconds || 0).toString(),
        regions: props.user.regions || [],
        createAccount: false,
        preferredGeoZones: props.user.preferredGeoZones,
        predictionModel: props.user.predictionModel,
      },

      accounts: [],
      accountIdSelected: null,
      fetchingAccounts: true,

      fieldErrors: {},
      errors: [],
      companyNameFromFetch: props.user.companyName,
      reqCompanyNameFromFetch:
        props.user.companyName && props.user.companyName.length > 0
          ? ReqEnum.SUCCESS
          : ReqEnum.PENDING,
      isCompanyInTestAccountsList: false,
      isCompanyInAccountsList: false,
      kindOfAccountSelection: KindOfAccountSelection.CHOOSE,
      resettingPassword: false,
    };

    this.escFunction = this.escFunction.bind(this);
    this.updateCarTypeMethod = this.updateCarTypeMethod.bind(this);
    this.updateUserIsLockedMethod = this.updateUserIsLockedMethod.bind(this);
    this.updateCourierTravelModeMethod =
      this.updateCourierTravelModeMethod.bind(this);
    this.updateStringMethod = this.updateStringMethod.bind(this);
    this.updateCourierAreaMethod = this.updateCourierAreaMethod.bind(this);
  }

  updateUser = (updatedUserFields: Partial<EditUser>): void => {
    const { user } = this.state;
    const updatedUser = {
      ...user,
      ...updatedUserFields,
    };
    this.setState({
      user: updatedUser,
      userUpdated: false,
    });

    if (IsThereAnyErrors(this.state.fieldErrors)) {
      const fieldErrors = validateCourierFields(
        updatedUser,
        this.state.reqCompanyNameFromFetch
      );
      this.setState({ fieldErrors: fieldErrors });
    }
  };

  updateUserByAccount = async (
    updatedUserFields: Partial<EditUser>
  ): Promise<void> => {
    const { user } = this.state;
    const { companyNumber } = updatedUserFields;

    const updatedUser = {
      ...user,
      ...updatedUserFields,
    };
    this.setState({ user: updatedUser, userUpdated: false });

    if (companyNumber) await this.getCompanyName(companyNumber);

    const validatedFieldErrors = validateCourierFields(
      updatedUser,
      this.state.reqCompanyNameFromFetch
    );

    if (IsThereAnyErrors(validatedFieldErrors)) {
      this.setState({ fieldErrors: validatedFieldErrors });
    }
  };

  escFunction(event: KeyboardEvent): void {
    if (event.keyCode === 27) {
      this.props.onCloseModal(this.state.userUpdated);
    }
  }

  async componentDidMount(): Promise<void> {
    document.addEventListener('keydown', this.escFunction, false);

    this.showCourierId();
    await this.getCompanyName(this.state.user.companyNumber);

    const userAccountId = this.props.user.accountId;
    const accounts = ((await fetchAccounts()) || []).filter(
      acc => acc.enabled || userAccountId === acc.id
    );
    this.setState({
      fetchingAccounts: false,
      accounts: accounts,
      accountIdSelected: this.props.user.accountId,
    });
  }

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.escFunction, false);
  }

  async saveUser(e: React.MouseEvent): Promise<void> {
    e.preventDefault();
    const { user, reqCompanyNameFromFetch } = this.state;

    const validatedFieldErrors = validateCourierFields(
      user,
      reqCompanyNameFromFetch
    );

    console.log(JSON.stringify(validatedFieldErrors));

    const ignoredFields = user.isLocked
      ? ['companyName', 'paymentByInvoiceAllowed']
      : [];

    this.setState({ fieldErrors: validatedFieldErrors });
    if (IsThereAnyErrors(validatedFieldErrors, ignoredFields)) {
      return;
    }

    this.setState({
      userUpdated: false,
      errors: [],
      updatingUser: true,
    });
    const result = await postUpdateUser(user, this.props.user.emailVerified);

    this.setState({
      userUpdated: !!result.success,
      errors: result.success ? [] : result.errors,
      updatingUser: false,
    });
  }

  async sendPasswordReset(e: React.MouseEvent): Promise<void> {
    e.preventDefault();
    this.setState({ resettingPassword: true });
    const { user } = this.state;
    const linkSent = await sendPasswordResetLink(user.email);
    if (linkSent) {
      this.props.onCloseModal(false);
    } else {
      this.setState({ resettingPassword: false });
    }
  }

  async showCourierId(): Promise<void> {
    const couriers = await fetchCouriers();
    const email = this.props.user.email;
    const courier = couriers.find(courier => courier.email === email);
    if (courier) {
      this.setState({ courierId: courier.id.toString() });
      this.updateUser({
        plateNumbers: courier.plateNumbers,
        notes: courier.notes,
      });
      this.setState({ currentPlateNumbers: courier.plateNumbers });
      this.setState({ currentNotes: courier.notes });
    } else {
      this.setState({ courierId: 'N/A' });
    }
    this.showCourierEtaStats(courier.id.toString());
  }

  async getCompanyName(orgnumberAsString: string): Promise<void> {
    const currentOrgNumber: string =
      getOrgNumberStringAsString(orgnumberAsString);

    this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
    if (isOrganisationNumberValid(currentOrgNumber, this.state.user.country)) {
      const { companyNameFromFetch, status } = await fetchCompanyName(
        currentOrgNumber,
        this.state.user.country
      );

      if (
        companyNameFromFetch &&
        companyNameFromFetch.length > 0 &&
        status.toString() !== '404'
      ) {
        this.setState({ companyNameFromFetch: companyNameFromFetch });
        this.setState({ reqCompanyNameFromFetch: ReqEnum.SUCCESS });
        this.setState({
          isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
            this.state.user,
            currentOrgNumber,
            this.state.accounts
          ),
          isCompanyInAccountsList: IsCompanyInAccountsList(
            this.state.user,
            currentOrgNumber,
            this.state.accounts
          ),
        });
      } else if (
        companyNameFromFetch &&
        companyNameFromFetch.length > 0 &&
        status.toString() === '500'
      )
        this.setState({ reqCompanyNameFromFetch: ReqEnum.ERROR });
      else {
        this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
      }
    } else {
      this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
      this.setState({ isCompanyInTestAccountsList: false });
    }
  }

  async showCourierEtaStats(courierId: string): Promise<void> {
    const etaResponse = await Fetch.get({
      url: `${settings.newApiUrl}/courier/${courierId}/eta-deviation`,
    });
    const etaNumber: number = await etaResponse.json();
    this.setState({ courierEtaDiff: etaNumber });
  }

  private parseTime(value: string): number {
    const v = parseInt(value.trim());
    return isNaN(v) ? 0 : v;
  }

  updateUserIsLockedMethod(checked: boolean): void {
    this.updateUser({ isLocked: checked });
  }

  updateCarTypeMethod(carType: CarType): void {
    this.updateUser({ carType: carType });
  }

  updateCourierTravelModeMethod(
    carType: CarType,
    travelMode: CourierTravelMode
  ): void {
    this.updateUser({ travelMode: travelMode, carType: carType });
  }

  updateCourierAreaMethod(newList: string[]): void {
    onFocusEvent('courier-area', 'dropdow');
    this.updateUser({ preferredGeoZones: newList });
  }

  updateStringMethod(keyName: string, name: string): void {
    const valueToUpdate: Partial<EditUser> = Object.assign({ [keyName]: name });
    this.updateUser(valueToUpdate);
  }

  render(): JSX.Element {
    const width = 800;

    const {
      user,
      errors,
      fieldErrors,
      userUpdated,
      updatingUser,
      courierId,
      fetchingAccounts,
      resettingPassword,
    } = this.state;

    const saveButton = {
      title: 'Save',
      disabled: updatingUser,
      onClick: (e: React.MouseEvent) => {
        this.saveUser(e);
      },
    };

    const resetPasswordButton = {
      title: 'Reset password',
      disabled: resettingPassword,
      onClick: (e: React.MouseEvent) => {
        this.sendPasswordReset(e);
      },
    };

    const buttons = userUpdated
      ? [resetPasswordButton]
      : [resetPasswordButton, saveButton];

    const isProduction = window.location.host.endsWith('porterbuddy.com');

    return (
      <Modal
        title="Edit user"
        onClose={() => this.props.onCloseModal(userUpdated)}
        isLoading={false}
        width={width}
        buttons={buttons}
      >
        <div className="edit-user-modal">
          <form className="edit-user-modal__form" autoComplete="off">
            <RoleRow role={user.role} />
            <EmailRow email={user.email} />
            <ShowCourierIdRow courierId={courierId} />
            <EmailVerified
              emailVerified={user.emailVerified}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                this.updateUser({
                  emailVerified: event.target.checked,
                })
              }
            />
            <EditInput
              value={user.newEmail}
              propsValue={this.props.user.email}
              errorMsg={fieldErrors.newEmail}
              keyName={'newEmail'}
              updateStringMethod={this.updateStringMethod}
              title={'New Email'}
              type={'email'}
              placeholder={'new-email@domain'}
            />
            <EditFormGroupModal hasError={!!fieldErrors.regions}>
              <label>
                <span className="edit-user-modal__form-label">Regions</span>
                <div className="edit-user-modal__form-checkboxes">
                  {RegionsList.map(regionItem => (
                    <label key={regionItem}>
                      <input
                        type="checkbox"
                        placeholder=""
                        name={`regions${regionItem}`}
                        checked={user.regions.includes(regionItem)}
                        onChange={event =>
                          this.updateUser({
                            regions: toggleElement(user.regions, regionItem),
                          })
                        }
                      />
                      <span>{capitalize(regionNames[regionItem])}</span>
                    </label>
                  ))}
                </div>
              </label>
              {fieldErrors.regions && <em>{fieldErrors.regions.message}</em>}
            </EditFormGroupModal>
            <EditInput
              value={user.firstName}
              propsValue={this.props.user.firstName}
              errorMsg={fieldErrors.firstName}
              keyName={'firstName'}
              updateStringMethod={this.updateStringMethod}
              title={'First Name'}
              type={'text'}
              placeholder={'name'}
            />
            <EditInput
              value={user.lastName}
              propsValue={this.props.user.lastName}
              errorMsg={fieldErrors.lastName}
              keyName={'lastName'}
              updateStringMethod={this.updateStringMethod}
              title={'Last Name'}
              type={'text'}
              placeholder={'name'}
            />
            <EditInput
              value={user.phoneCode}
              propsValue={this.props.user.phoneCode}
              errorMsg={fieldErrors.phoneCode}
              keyName={'phoneCode'}
              updateStringMethod={this.updateStringMethod}
              title={'Phone country code'}
              type={'tel'}
              placeholder={'+47'}
            />
            <EditInput
              value={user.phoneNumber}
              propsValue={this.props.user.phoneNumber}
              errorMsg={fieldErrors.phoneNumber}
              keyName={'phoneNumber'}
              updateStringMethod={this.updateStringMethod}
              title={'Phone number'}
              type={'tel'}
              placeholder={''}
            />
            <EditFormGroupModal hasError={!!fieldErrors.country}>
              <label>
                <span className="edit-user-modal__form-label">Country</span>
                <select
                  onChange={event => {
                    const newUser = { ...this.state.user };
                    newUser.country = event.target.value as string;
                    const countrySwitch =
                      newUser.country !== this.props.user.country;
                    const accounts = this.state.accounts;
                    const companyNumber = this.props.user.companyNumber;
                    newUser.createAccount = false;
                    if (countrySwitch) {
                      newUser.companyNumber = '';
                      newUser.companyName = '';
                    } else {
                      newUser.companyNumber = companyNumber || '';
                      newUser.companyName = this.props.user.companyName || '';
                    }
                    this.setState({
                      kindOfAccountSelection: KindOfAccountSelection.CHOOSE,
                      accountIdSelected: this.props.user.accountId,
                      companyNameFromFetch: countrySwitch
                        ? ''
                        : this.props.user.companyName,
                      user: newUser,
                      reqCompanyNameFromFetch: ReqEnum.SUCCESS,
                    });
                    this.setState({
                      isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
                        newUser,
                        companyNumber,
                        accounts
                      ),
                    });
                  }}
                  value={user.country}
                >
                  {Object.keys(supportedCountries).map(countryName => (
                    <option key={countryName}>
                      {supportedCountries[countryName].name}
                    </option>
                  ))}
                </select>
              </label>
            </EditFormGroupModal>
            <SwedenInfo country={this.state.user.country} />
            {isProduction && (
              <div className={'warning'}>
                Organisation numbers must be updated in Dipps. Dipps will
                automatically update the value in Dispatch, ensuring that the
                correct company will be payed.
              </div>
            )}
            <div className={'edit-user-modal__form-group'}>
              <AccountsChooseOrCreateRadioButtons
                disabled={isProduction}
                classNameSpan={'edit-user-modal__form-label'}
                classNameDiv={'edit-user-modal__form-radio normal'}
                checkedUseExistingAccount={
                  this.state.kindOfAccountSelection ===
                  KindOfAccountSelection.CHOOSE
                }
                checkedCreateNewAccount={
                  this.state.kindOfAccountSelection ===
                  KindOfAccountSelection.CREATE
                }
                onChangeChooseExistingAccount={() => {
                  const newUser = { ...this.state.user };
                  const countrySwitch =
                    newUser.country !== this.props.user.country;
                  const accounts = this.state.accounts;
                  const companyNumber = this.props.user.companyNumber;
                  newUser.createAccount = false;
                  if (countrySwitch) {
                    newUser.companyNumber = '';
                    newUser.companyName = '';
                  } else {
                    newUser.companyNumber = companyNumber || '';
                    newUser.companyName = this.props.user.companyName || '';
                  }
                  this.setState({
                    kindOfAccountSelection: KindOfAccountSelection.CHOOSE,
                    accountIdSelected: this.props.user.accountId,
                    companyNameFromFetch: countrySwitch
                      ? ''
                      : this.props.user.companyName,
                    user: newUser,
                    reqCompanyNameFromFetch: ReqEnum.SUCCESS,
                  });
                  this.setState({
                    isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
                      newUser,
                      companyNumber,
                      accounts
                    ),
                  });
                }}
                onChangeChooseCreateNewAccount={() => {
                  const accounts = this.state.accounts;
                  const companyNumber = this.props.user.companyNumber;
                  const newUser = { ...this.state.user };
                  newUser.createAccount = true;
                  newUser.companyNumber = this.props.user.companyNumber || '';
                  newUser.companyName = this.props.user.companyName || '';
                  this.setState({
                    kindOfAccountSelection: KindOfAccountSelection.CREATE,
                    accountIdSelected: this.props.user.accountId,
                    companyNameFromFetch: this.props.user.companyName,
                    user: newUser,
                  });
                  this.setState({
                    isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
                      newUser,
                      companyNumber,
                      accounts
                    ),
                  });
                }}
              />
            </div>
            {this.state.kindOfAccountSelection ===
              KindOfAccountSelection.CHOOSE && (
              <AccountRow
                classNameSpan={'edit-user-modal__form-label'}
                classNameDiv={'edit-user-modal__form-radio normal'}
                fetchingAccounts={fetchingAccounts}
                accounts={this.state.accounts}
                updateUser={this.updateUserByAccount}
                accountId={this.props.user.accountId}
                userToUpdate={this.props.user}
                newUser={this.state.user}
                filterForCountry={true}
              />
            )}
            <EditFormGroupModal hasError={!!fieldErrors.companyName}>
              <label>
                <span className="edit-user-modal__form-label">
                  Company name
                </span>
                {this.state.user.country === 'Norway' ? (
                  <input
                    style={{
                      borderColor:
                        this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                          ? 'black'
                          : this.state.reqCompanyNameFromFetch ===
                            ReqEnum.PENDING
                          ? 'yellow'
                          : 'red',
                    }}
                    type="text"
                    data-lpignore="true"
                    placeholder=""
                    name="companyName"
                    value={
                      this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                        ? this.state.companyNameFromFetch.toString()
                        : this.state.reqCompanyNameFromFetch === ReqEnum.PENDING
                        ? 'make sure the company number is written correct'
                        : `Saved: ${this.state.user.companyName}, there is a network error to www.brreg.no/`
                    }
                    disabled={true}
                    className={
                      this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                        ? EDIT_USER_MODIFIED_CLASS
                        : undefined
                    }
                    autoComplete="none"
                    readOnly={
                      fetchingAccounts ||
                      this.state.kindOfAccountSelection ===
                        KindOfAccountSelection.CHOOSE
                    }
                  />
                ) : (
                  <input
                    type="txt"
                    name="companyName"
                    value={this.state.user.companyName}
                    onChange={event =>
                      this.updateUser({
                        companyName: event.target.value,
                      })
                    }
                    readOnly={
                      fetchingAccounts ||
                      this.state.kindOfAccountSelection ===
                        KindOfAccountSelection.CHOOSE
                    }
                  />
                )}
              </label>
              {this.state.kindOfAccountSelection ===
                KindOfAccountSelection.CREATE && (
                <>
                  <ShowTestAccountIfItExists
                    isCompanyInTestAccountsList={
                      this.state.isCompanyInTestAccountsList
                    }
                    isCompanyInAccountsList={this.state.isCompanyInAccountsList}
                    isIdenticalCompanyNumber={
                      this.state.user.companyNumber ===
                      this.props.user.companyNumber
                    }
                    role={this.state.user.role}
                  />
                  {fieldErrors.companyName && (
                    <em>{fieldErrors.companyName.message}</em>
                  )}
                </>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.companyNumber}>
              <label>
                <span className="edit-user-modal__form-label">
                  Organization number
                </span>
                <input
                  type="text"
                  data-lpignore="true"
                  placeholder=""
                  name="companyNumber"
                  value={user.companyNumber}
                  onChange={async event => {
                    const currentOrgNumber = event.target.value;
                    await this.getCompanyName(currentOrgNumber);

                    const companyNameToUpdate =
                      this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                        ? this.state.companyNameFromFetch
                        : this.state.user.companyName;

                    const updatedUserFields: Partial<EditUser> = {
                      companyNumber:
                        getOrgNumberStringAsString(currentOrgNumber),
                      companyName: companyNameToUpdate,
                    };

                    this.updateUser(updatedUserFields);
                  }}
                  className={
                    this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                  readOnly={
                    fetchingAccounts ||
                    this.state.kindOfAccountSelection ===
                      KindOfAccountSelection.CHOOSE
                  }
                />
              </label>
              {fieldErrors.companyNumber && (
                <em>{fieldErrors.companyNumber.message}</em>
              )}
            </EditFormGroupModal>
            {this.state.kindOfAccountSelection ===
              KindOfAccountSelection.CHOOSE && (
              <EditFormGroupModal hasError={false}>
                <label>
                  <span className="edit-user-modal__form-label">
                    Compensation Model
                  </span>
                  <input
                    type="text"
                    readOnly={true}
                    value={capitalize(
                      this.state.accounts.find(
                        acc => this.state.accountIdSelected === acc.id
                      )?.compensationModel || ''
                    )}
                  />
                </label>
              </EditFormGroupModal>
            )}
            <EditFormGroupModal hasError={!!fieldErrors.etaOffsetSeconds}>
              <label>
                <span className="edit-user-modal__form-label">
                  <span
                    style={{
                      textDecoration:
                        user.predictionModel !== 'DISPATCHER'
                          ? 'line-through'
                          : 'None',
                    }}
                  >
                    ETA stop offset
                  </span>
                </span>
                <div style={{ width: '100%' }}>
                  <input
                    type="text"
                    data-lpignore="true"
                    minLength={1}
                    maxLength={4}
                    placeholder="Sec"
                    name="etaOffset"
                    value={user.etaOffsetSecondsInput}
                    onChange={event => {
                      const seconds = this.parseTime(event.target.value);
                      this.updateUser({
                        etaOffsetSeconds: isNaN(seconds) ? 0 : seconds,
                        etaOffsetSecondsInput: event.target.value,
                      });
                    }}
                    onFocus={() => onFocusEvent('eta-offset', 'input-box')}
                    style={{ width: '5ch' }}
                    autoComplete="none"
                    readOnly={fetchingAccounts}
                  />{' '}
                  seconds{' '}
                  <span className="edit-user-modal__form-etaoffset">
                    {formatEtaOffset(user.etaOffsetSeconds)}
                  </span>
                </div>
              </label>
              <div style={{ marginLeft: '15ch' }}>
                Current average ETA deviation (in seconds) for last 6 bundles
                is: {this.state.courierEtaDiff}
              </div>
              {fieldErrors.etaOffsetSeconds && (
                <em>{fieldErrors.etaOffsetSeconds.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.tasks}>
              <label>
                <span className="edit-user-modal__form-label">Task(s)</span>
                <div className="edit-user-modal__form-checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      placeholder=""
                      name={`task-courier`}
                      checked={user.isCourier}
                      onChange={() =>
                        this.updateUser({ isCourier: !user.isCourier })
                      }
                    />
                    <span>Delivery</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      placeholder=""
                      name={`task-sorter`}
                      checked={user.isSorter}
                      onChange={() =>
                        this.updateUser({ isSorter: !user.isSorter })
                      }
                    />
                    <span>Sorting</span>
                  </label>
                </div>
              </label>
              {fieldErrors.tasks && <em>{fieldErrors.tasks.message}</em>}
            </EditFormGroupModal>
            {this.state.user.isCourier ? (
              <ShowVehicle
                currentPlateNumbers={this.state.currentPlateNumbers}
                plateNumbers={user.plateNumbers}
                carType={this.state.user.carType}
                updateUser={this.updateUser}
              />
            ) : null}
            <ShowCourierArea
              preferredGeoZonesFieldErrors={fieldErrors.preferredGeoZones}
              updateCourierAreaMethod={this.updateCourierAreaMethod}
              courierAreaList={user.preferredGeoZones}
            />
            <NotesRow
              notes={user.notes ? user.notes : ''}
              onChange={event => {
                this.updateUser({ notes: event.target.value });
              }}
            />

            <LockUser
              isLocked={user.isLocked}
              updateUserIsLockedMethod={this.updateUserIsLockedMethod}
            />
            <UpdateFeedback
              updatingUser={updatingUser}
              errors={errors}
              userUpdated={userUpdated}
            />
          </form>
        </div>
      </Modal>
    );
  }
}
